import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/auth/useAuth';
import {
  fetchConnectionsJson,
  fetchConnectionsDraftJson,
  saveConnectionsJson,
  saveDraftConnectionsJson,
} from '@/services/logicAppsApi';
import { type ConnectionsConfiguration } from '@/types/logicApps';

export const useConnectionsJson = (info: { projectName: string; appName: string; appId: string } | null) => {
  const { getOttoToken, isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ['connections', info?.projectName?.toLowerCase(), info?.appName?.toLowerCase()],
    queryFn: async () => {
      if (!info?.projectName || !info?.appName) {
        return {};
      }

      return fetchConnectionsJson({
        projectName: info.projectName,
        appName: info.appName,
        getToken: getOttoToken,
      });
    },
    enabled: isAuthenticated && !!info?.projectName && !!info?.appName,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useConnectionsDraftJson = (info: { projectName: string; appName: string; appId: string } | null) => {
  const { getOttoToken, isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ['connections-draft', info?.projectName?.toLowerCase(), info?.appName?.toLowerCase()],
    queryFn: async () => {
      if (!info?.projectName || !info?.appName) {
        return {};
      }

      return fetchConnectionsDraftJson({
        projectName: info.projectName,
        appName: info.appName,
        getToken: getOttoToken,
      });
    },
    enabled: isAuthenticated && !!info?.projectName && !!info?.appName,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};


/**
 * Mutation hook to persist the full connections.json contents for an app.
 * Invalidates both the production and draft caches plus the managed connections
 * list so editor + designer surfaces re-fetch the canonical contents.
 */
export const useSaveConnectionsJson = (
  info: { projectName: string; appName: string; appId: string } | null,
) => {
  const { getOttoToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (connectionsConfig: ConnectionsConfiguration) => {
      if (!info?.appName || !info?.projectName) {
        throw new Error('Project and app name are required to save connections');
      }
      return saveConnectionsJson({
        projectName: info.projectName,
        appName: info.appName,
        getToken: getOttoToken,
        connectionsConfig,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['connections', info?.projectName?.toLowerCase(), info?.appName?.toLowerCase()] });
      void queryClient.invalidateQueries({ queryKey: ['connections-draft', info?.projectName?.toLowerCase(), info?.appName?.toLowerCase()] });
      void queryClient.invalidateQueries({ queryKey: ['connections'] });
    },
  });
};

/**
 * Mutation hook to persist the full connections-draft.json contents for an app.
 * Invalidates both the production and draft caches plus the managed connections
 * list so editor + designer surfaces re-fetch the canonical contents.
 */
export const useSaveDraftConnectionsJson = (
  info: { projectName: string; appName: string; appId: string } | null,
) => {
  const { getOttoToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (connectionsConfig: ConnectionsConfiguration) => {
      if (!info?.appName || !info?.projectName) {
        throw new Error('Project and app name are required to save connections');
      }
      return saveDraftConnectionsJson({
        projectName: info.projectName,
        appName: info.appName,
        getToken: getOttoToken,
        connectionsConfig,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['connections-draft', info?.projectName?.toLowerCase(), info?.appName?.toLowerCase()] });
      void queryClient.invalidateQueries({ queryKey: ['connections'] });
    },
  });
};
